import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  TextField,
  AppBar,
  Toolbar,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  LinearProgress,
  FormControlLabel,
  Checkbox,
  CardActions,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Dashboard,
  SportsTennis,
  People,
  Add,
  Menu as MenuIcon,
  Logout,
  Home,
  Edit,
  Delete,
  CalendarToday,
  LocationOn,
  AttachMoney
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useMsal } from '@azure/msal-react';
import { useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { tournamentAPI } from '../services/api';

const TournamentManagement = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();

  // Add CSS keyframes for animations
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes rotate {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
      }
      @keyframes pulse {
        0%, 100% { transform: scale(1); }
        50% { transform: scale(1.05); }
      }
      @keyframes fadeInUp {
        from { opacity: 0; transform: translateY(30px); }
        to { opacity: 1; transform: translateY(0); }
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);
  const { instance } = useMsal();
  
  // Local state for data
  const [tournaments, setTournaments] = useState([]);
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // API functions
  const loadTournaments = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('Loading tournaments...');
      const response = await tournamentAPI.getAll();
      console.log('Tournaments response:', response);
      console.log('Tournaments data:', response.data);
      // The API returns { data: [...], pageIndex: 0, totalPages: 1, ... }
      // So we need to access response.data.data for the actual tournaments array
      setTournaments(response.data?.data || []);
    } catch (error) {
      console.error('Error loading tournaments:', error);
      console.error('Error details:', error.response?.data);
      setError(`Failed to load tournaments: ${error.message}`);
      setTournaments([]);
    } finally {
      setLoading(false);
    }
  };

  const loadPlayers = async () => {
    try {
      // For now, set empty array since we don't have a players API endpoint
      setPlayers([]);
    } catch (error) {
      console.error('Error loading players:', error);
      setPlayers([]);
    }
  };

  const addTournament = async (tournamentData) => {
    try {
      setLoading(true);
      setError(null);
      const response = await tournamentAPI.create(tournamentData);
      console.log('Tournament created:', response);
      // Reload tournaments after creating
      await loadTournaments();
      // Show success notification
      console.log('Tournament created successfully!');
    } catch (error) {
      console.error('Error creating tournament:', error);
      setError(`Failed to create tournament: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const updateTournament = async (tournamentData) => {
    try {
      setLoading(true);
      setError(null);
      const response = await tournamentAPI.update(tournamentData.id, tournamentData);
      console.log('Tournament updated:', response);
      // Reload tournaments after updating
      await loadTournaments();
      // Show success notification
      console.log('Tournament updated successfully!');
    } catch (error) {
      console.error('Error updating tournament:', error);
      setError(`Failed to update tournament: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const deleteTournament = async (tournamentId) => {
    try {
      setLoading(true);
      setError(null);
      const response = await tournamentAPI.delete(tournamentId);
      console.log('Tournament deleted:', response);
      // Reload tournaments after deleting
      await loadTournaments();
      // Show success notification
      console.log('Tournament deleted successfully!');
    } catch (error) {
      console.error('Error deleting tournament:', error);
      setError(`Failed to delete tournament: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Handle logout
  const logout = async () => {
    try {
      await instance.logoutRedirect({
        postLogoutRedirectUri: window.location.origin,
      });
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };
  
  const [drawerOpen, setDrawerOpen] = useState(!isMobile);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTournament, setEditingTournament] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [tournamentToDelete, setTournamentToDelete] = useState(null);
  
  const DEFAULT_ACKNOWLEDGMENT_TEXT = 'I have read, understood, and agree to the VeiledKnights Tournament Waiver, Release of Liability & Data Consent. I acknowledge that by submitting this registration, I am providing my electronic signature and giving up certain legal rights, including the right to sue, in the State of <ADD_STATE_HERE>. I also consent to VeiledKnights collecting and storing my personal data for tournament administration, compliance, and communication purposes.';

  const DEFAULT_WAIVER_TEXT = `PLEASE READ CAREFULLY. THIS IS A RELEASE OF LIABILITY, WAIVER OF CERTAIN LEGAL RIGHTS, AND CONSENT TO DATA COLLECTION WITH VEILEDKNIGHTS.
This Waiver and Release of Liability ("Agreement") is entered into between the undersigned participant ("Participant") and VeiledKnights, its officers, directors, employees, volunteers, affiliates, sponsors, and agents (collectively, the "Releasees").
By registering for and participating in this tournament, I acknowledge and agree to the following:
1. Assumption of Risk
I understand that participation in this tournament involves strenuous physical activity and may include risks of serious injury, illness, disability, or death, including but not limited to sprains, strains, fractures, collisions, exposure to weather, and accidents related to facilities, equipment, or other participants. I voluntarily assume all risks, both known and unknown, associated with my participation.
2. Release and Waiver
I hereby RELEASE, WAIVE, DISCHARGE, AND COVENANT NOT TO SUE VeiledKnights and all Releasees from any and all liability, claims, demands, or actions arising from my participation, including injury, disability, death, or damage to personal property, whether caused by the negligence of the Releasees or otherwise, to the fullest extent permitted by law.
3. Indemnification
I agree to INDEMNIFY and HOLD HARMLESS the Releasees from any and all claims, damages, costs, and expenses (including attorney's fees) arising from my participation in this tournament.
4. Medical Acknowledgment
I represent that I am physically fit to participate, have no medical condition that would prevent safe participation, and will seek medical clearance if necessary. I authorize emergency medical care in case of injury or illness during the tournament and accept full financial responsibility for any resulting costs.
5. Rules and Conduct
I agree to abide by all tournament rules, regulations, and instructions provided by VeiledKnights. I understand that unsportsmanlike conduct or failure to follow rules may result in disqualification or removal without refund.
6. Photo/Video Release
I consent to the use of my name, voice, likeness, and image in photos, videos, and media taken during the tournament for promotional, marketing, or reporting purposes without compensation.
7. Registration Fees
I understand that registration fees are non-refundable except if the tournament is cancelled by VeiledKnights organizers.
8. Governing Law
This Agreement shall be governed by and construed under the laws of the State of <ADD_STATE_HERE>, to the maximum extent permitted by law. Any disputes shall be resolved exclusively in the state or federal courts located in <ADD_STATE_HERE>.
9. Severability
If any portion of this Agreement is deemed invalid or unenforceable, the remainder shall remain in full force and effect.
10. Final Certification
I certify that I have completely read and understand this Agreement and its terms. Before submitting my registration, I had the opportunity to ask any questions about this Agreement. I acknowledge that, by completing registration, I voluntarily assume all risks and waive and release certain rights that I, and each of my heirs, next of kin, family, relatives, guardians, conservators, executors, administrators, trustees, and assigns may have against the Releasees.
11. Eligibility Revocation
I understand and agree that VeiledKnights reserves the right to revoke my eligibility to play, coach, or participate in any tournament activity in the event of any violation of this Agreement, tournament rules, or Code of Conduct.
12. Data Collection and Privacy
I consent to VeiledKnights collecting and storing my personal data (including name, contact details, demographics, registration details, IP address, and consent record) for tournament administration and compliance purposes. I understand this information may also be used to communicate event updates and related information. If the Participant is under the age of 18, this consent must be given by a parent or legal guardian on their behalf.`;

  const [tournamentData, setTournamentData] = useState({
    name: '',
    sport: '',
    startDate: '',
    endDate: '',
    location: '',
    maxParticipants: '',
    description: '',
    registrationDeadline: '',
    status: 'active',
    publicViewEnabled: true,
    waiver: DEFAULT_WAIVER_TEXT,
    acknowledgment: DEFAULT_ACKNOWLEDGMENT_TEXT,
    registrationFee: '',
    currencyCode: 'USD',
    registrationFeePaymentUrl: '',
    registrationFeePaymentSystem: 'Zelle'
  });

  const sports = ['Pickleball', 'Badminton', 'Basketball'];
  const statuses = ['active', 'inactive'];
  const currencies = ['USD', 'EUR', 'GBP', 'CAD', 'AUD'];
  const paymentSystems = ['Zelle', 'PayPal', 'Venmo', 'Cash App', 'Bank Transfer'];

  // Utility function to calculate current registrations for a tournament
  const getCurrentRegistrations = (tournamentId) => {
    return players.filter(player => player.tournamentId === tournamentId).length;
  };

  const menuItems = [
    { text: 'Dashboard', icon: <Dashboard />, path: '/admin' },
    { text: 'Tournaments', icon: <SportsTennis />, path: '/admin/tournaments' },
    { text: 'Players', icon: <People />, path: '/admin/players' }
  ];

  // Load tournaments when component mounts
  useEffect(() => {
    loadTournaments();
    loadPlayers();
  }, []);

  const handleCreateTournament = () => {
    setEditingTournament(null);
    setTournamentData({
      name: '',
      sport: '',
      startDate: '',
      endDate: '',
      location: '',
      maxParticipants: '',
      description: '',
      registrationDeadline: '',
      status: 'active',
      publicViewEnabled: true,
      waiver: DEFAULT_WAIVER_TEXT,
      acknowledgment: DEFAULT_ACKNOWLEDGMENT_TEXT,
      registrationFee: '',
      currencyCode: 'USD',
      registrationFeePaymentUrl: '',
      registrationFeePaymentSystem: 'Zelle'
    });
    setDialogOpen(true);
  };

  const handleEditTournament = (tournament) => {
    setEditingTournament(tournament);
    
    // Helper function to safely convert milliseconds to YYYY-MM-DD
    const formatDate = (timestamp) => {
      if (!timestamp || isNaN(timestamp)) {
        return '';
      }
      const date = new Date(timestamp);
      if (isNaN(date.getTime())) {
        return '';
      }
      return date.toISOString().split('T')[0];
    };
    
    setTournamentData({
      name: tournament.name,
      sport: tournament.sport,
      startDate: formatDate(tournament.startDate),
      endDate: formatDate(tournament.endDate),
      location: tournament.location,
      maxParticipants: tournament.maxParticipants.toString(),
      description: tournament.description || '',
      registrationDeadline: formatDate(tournament.registrationDeadline),
      status: tournament.status,
      publicViewEnabled: tournament.publicViewEnabled,
      waiver: tournament.waiver || '',
      acknowledgment: tournament.acknowledgment || '',
      registrationFee: tournament.registrationFee ? tournament.registrationFee.toString() : '',
      currencyCode: tournament.currencyCode || 'USD',
      registrationFeePaymentUrl: tournament.registrationFeePaymentUrl || '',
      registrationFeePaymentSystem: tournament.registrationFeePaymentSystem || 'Zelle'
    });
    setDialogOpen(true);
  };


  const handleSaveTournament = () => {
    // Helper function to safely convert YYYY-MM-DD to milliseconds
    const parseDate = (dateString) => {
      if (!dateString) {
        return null;
      }
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return null;
      }
      return date.getTime();
    };
    
    const startDate = parseDate(tournamentData.startDate);
    const endDate = parseDate(tournamentData.endDate);
    const registrationDeadline = parseDate(tournamentData.registrationDeadline);
    
    // Validate required date fields
    if (!startDate || !endDate || !registrationDeadline) {
      alert('Please provide valid dates for all required fields.');
      return;
    }
    
    const now = Date.now();
    const tournament = {
      ...tournamentData,
      maxParticipants: parseInt(tournamentData.maxParticipants),
      startDate: startDate,
      endDate: endDate,
      registrationDeadline: registrationDeadline,
      registrationFee: tournamentData.registrationFee ? parseFloat(tournamentData.registrationFee) : null,
      createdAt: editingTournament ? editingTournament.createdAt : now,
      updatedAt: now // Always set to current timestamp for both create and update
    };

    if (editingTournament) {
      updateTournament({ ...tournament, id: editingTournament.id });
    } else {
      // Generate UUID for new tournaments
      const uuid = uuidv4();
      console.log('Generated UUID for new tournament:', uuid);
      addTournament({ ...tournament, id: uuid });
    }

    setDialogOpen(false);
    setEditingTournament(null);
  };

  const handleDeleteTournament = (tournament) => {
    setTournamentToDelete(tournament);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (tournamentToDelete) {
      deleteTournament(tournamentToDelete.id);
      console.log(`Tournament "${tournamentToDelete.name}" has been deleted successfully`);
      setDeleteDialogOpen(false);
      setTournamentToDelete(null);
    }
  };

  const cancelDelete = () => {
    setDeleteDialogOpen(false);
    setTournamentToDelete(null);
  };

  const getFillPercentage = (tournament) => {
    const currentRegistrations = getCurrentRegistrations(tournament.id);
    return Math.round((currentRegistrations / tournament.maxParticipants) * 100);
  };


  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* App Bar */}
      <AppBar 
        position="fixed" 
        sx={{ 
          zIndex: theme.zIndex.drawer + 1,
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(10px)',
          borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
          boxShadow: '0 2px 20px rgba(0, 0, 0, 0.1)'
        }}
      >
        <Toolbar>
          <IconButton
            color="primary"
            edge="start"
            onClick={() => setDrawerOpen(!drawerOpen)}
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            <img
              src={require('../images/new_logo.png')}
              alt="VeiledKnights Logo"
              style={{
                width: '40px',
                height: '40px',
                marginRight: '12px',
                borderRadius: '0px'
              }}
            />
            <Typography 
              variant="h6" 
              component="div" 
              sx={{ 
                color: '#1a365d',
                fontWeight: 700,
                letterSpacing: '-0.01em'
              }}
            >
              VeiledKnights
            </Typography>
          </Box>
          <Button 
            color="primary" 
            onClick={logout} 
            startIcon={<Logout />}
            sx={{
              fontWeight: 600,
              textTransform: 'none',
              borderRadius: 2,
              px: 2,
              py: 1,
              '&:hover': {
                backgroundColor: 'rgba(25, 118, 210, 0.08)',
                transform: 'translateY(-1px)'
              },
              transition: 'all 0.3s ease'
            }}
          >
            Logout
          </Button>
        </Toolbar>
      </AppBar>

      {/* Drawer */}
      <Drawer
        variant={isMobile ? 'temporary' : 'persistent'}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        sx={{
          width: 280,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: 280,
            boxSizing: 'border-box',
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)',
            backdropFilter: 'blur(20px)',
            borderRight: '1px solid rgba(255, 255, 255, 0.2)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
          },
        }}
      >
        <Toolbar />
        <Box sx={{ overflow: 'auto', height: '100%' }}>
          {/* Admin Portal Header */}
          <Box sx={{ 
            px: 3, 
            py: 2, 
            textAlign: 'center',
            borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
            mb: 1
          }}>
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 700,
                color: '#1a365d',
                background: 'linear-gradient(135deg, #1a365d 0%, #1976d2 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}
            >
              Admin Portal
            </Typography>
          </Box>

          {/* Divider */}
          <Box sx={{ 
            mx: 2, 
            mb: 1,
            height: '2px',
            background: 'linear-gradient(90deg, transparent 0%, #1976d2 50%, transparent 100%)',
            borderRadius: '1px'
          }} />

          {/* Navigation Menu */}
          <List sx={{ px: 2 }}>
            {menuItems.map((item, index) => (
              <ListItem 
                key={item.text}
                onClick={() => {
                  navigate(item.path);
                  if (isMobile) setDrawerOpen(false);
                }}
                sx={{
                  borderRadius: 2,
                  mb: 1,
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  background: item.path === '/admin/tournaments' 
                    ? 'linear-gradient(135deg, rgba(25, 118, 210, 0.15) 0%, rgba(25, 118, 210, 0.08) 100%)'
                    : 'transparent',
                  '&:hover': {
                    background: 'linear-gradient(135deg, rgba(25, 118, 210, 0.1) 0%, rgba(25, 118, 210, 0.05) 100%)',
                    transform: 'translateX(4px)',
                    boxShadow: '0 4px 15px rgba(25, 118, 210, 0.2)'
                  }
                }}
              >
                <ListItemIcon sx={{ 
                  minWidth: 40,
                  color: '#1976d2',
                  '& .MuiSvgIcon-root': {
                    fontSize: 24
                  }
                }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText 
                  primary={item.text}
                  sx={{
                    '& .MuiTypography-root': {
                      fontWeight: 600,
                      color: '#1a365d',
                      fontSize: '0.95rem'
                    }
                  }}
                />
              </ListItem>
            ))}
          </List>

          <Divider sx={{ mx: 2, my: 2, borderColor: 'rgba(255, 255, 255, 0.2)' }} />

          {/* Public Site Link */}
          <List sx={{ px: 2 }}>
            <ListItem 
              onClick={() => navigate('/')}
              sx={{
                borderRadius: 2,
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                '&:hover': {
                  background: 'linear-gradient(135deg, rgba(25, 118, 210, 0.1) 0%, rgba(25, 118, 210, 0.05) 100%)',
                  transform: 'translateX(4px)',
                  boxShadow: '0 4px 15px rgba(25, 118, 210, 0.2)'
                }
              }}
            >
              <ListItemIcon sx={{ 
                minWidth: 40,
                color: '#1976d2',
                '& .MuiSvgIcon-root': {
                  fontSize: 24
                }
              }}>
                <Home />
              </ListItemIcon>
              <ListItemText 
                primary="Public Site"
                sx={{
                  '& .MuiTypography-root': {
                    fontWeight: 600,
                    color: '#1a365d',
                    fontSize: '0.95rem'
                  }
                }}
              />
            </ListItem>
          </List>
        </Box>
      </Drawer>

      {/* Main Content */}
      <Box 
        component="main" 
        sx={{ 
          flexGrow: 1, 
          p: 3, 
          mt: 8,
          background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
          minHeight: 'calc(100vh - 64px)'
        }}
      >
        <Container maxWidth="xl">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Box sx={{ 
              textAlign: 'center', 
              mb: 6,
              background: 'rgba(255, 255, 255, 0.7)',
              borderRadius: 3,
              p: 4,
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
            }}>
              <Typography 
                variant="h3" 
                component="h1" 
                sx={{ 
                  fontWeight: 800, 
                  mb: 2,
                  background: 'linear-gradient(135deg, #1a365d 0%, #1976d2 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  letterSpacing: '-0.02em'
                }}
              >
                Tournament Management
              </Typography>
              <Typography 
                variant="h6" 
                sx={{ 
                  color: '#4a5568',
                  fontWeight: 400,
                  maxWidth: '600px',
                  mx: 'auto',
                  mb: 3
                }}
              >
                Create, edit, and manage tournaments
              </Typography>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={handleCreateTournament}
                size="large"
                sx={{
                  px: 4,
                  py: 1.5,
                  fontSize: '1rem',
                  fontWeight: 600,
                  borderRadius: 2,
                  textTransform: 'none',
                  background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                  boxShadow: '0 4px 15px rgba(25, 118, 210, 0.3)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
                    boxShadow: '0 6px 20px rgba(25, 118, 210, 0.4)',
                    transform: 'translateY(-2px)'
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                Create Tournament
              </Button>
            </Box>
          </motion.div>

          {/* Tournament Overview */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <Box sx={{ width: '95%', mx: 'auto' }}>
              {/* Tournament Cards Container */}
              <Box sx={{ 
                background: 'rgba(255, 255, 255, 0.9)',
                backdropFilter: 'blur(10px)',
                borderRadius: 3,
                border: '1px solid rgba(255, 255, 255, 0.2)',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                p: { xs: 3, sm: 4, md: 5 },
                mb: 4,
                overflow: 'hidden'
              }}>
                <Typography 
                  variant="h5" 
                  component="h2" 
                  gutterBottom 
                  sx={{ 
                    fontWeight: 700, 
                    mb: 4, 
                    textAlign: 'center',
                    color: '#1a365d',
                    background: 'linear-gradient(135deg, #1a365d 0%, #1976d2 100%)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent'
                  }}
                >
                  Tournament Management
                </Typography>
                <Grid container spacing={3} sx={{ alignItems: 'stretch', justifyContent: 'center' }}>
                  {loading ? (
                    <Grid item xs={12} sx={{ textAlign: 'center', py: 4 }}>
                      <Typography variant="h6">Loading tournaments...</Typography>
                    </Grid>
                  ) : error ? (
                    <Grid item xs={12} sx={{ textAlign: 'center', py: 4 }}>
                      <Typography variant="h6" color="error">
                        {error}
                      </Typography>
                    </Grid>
                  ) : tournaments && tournaments.length > 0 ? (
                    tournaments.map((tournament, index) => (
                    <Grid item xs={12} sm={6} md={6} key={tournament.id} sx={{ display: 'flex', justifyContent: 'center' }}>
                      <motion.div
                        initial={{ opacity: 0, y: 50 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: index * 0.1 }}
                        style={{ width: '100%', maxWidth: '320px' }}
                      >
                        <Card 
                          sx={{ 
                            height: { xs: '750px', sm: '520px' }, // Significantly increased height on mobile
                            width: '100%',
                            minWidth: '280px',
                            maxWidth: '320px',
                            display: 'flex', 
                            flexDirection: 'column',
                            borderRadius: 3,
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                            background: 'rgba(255, 255, 255, 0.95)',
                            backdropFilter: 'blur(10px)',
                            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                              transform: 'translateY(-8px)',
                              boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)',
                              borderColor: '#1976d2'
                            }
                          }}
                        >
                          <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', height: '100%', p: 3 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                              <Typography 
                                variant="h6" 
                                component="h3" 
                                sx={{ 
                                  fontWeight: 700, 
                                  fontSize: '1.1rem', 
                                  lineHeight: 1.3,
                                  color: '#1a365d'
                                }}
                              >
                                {tournament.name}
                              </Typography>
                              <Chip 
                                label={tournament.sport} 
                                sx={{ 
                                  fontWeight: 600,
                                  background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
                                  color: 'white',
                                  border: 'none',
                                  boxShadow: '0 2px 8px rgba(25, 118, 210, 0.3)'
                                }}
                                size="small"
                              />
                            </Box>
                            
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                mb: 2, 
                                fontSize: '0.875rem', 
                                lineHeight: 1.4, 
                                minHeight: '40px',
                                color: '#4a5568',
                                fontWeight: 500
                              }}
                            >
                              {tournament.description || `Join us for an exciting ${tournament.sport} tournament!`}
                            </Typography>

                            <Box sx={{ mb: 2 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <CalendarToday sx={{ mr: 1, fontSize: 16, color: '#1976d2' }} />
                                <Typography variant="body2" sx={{ fontSize: '0.8rem', color: '#4a5568', fontWeight: 500 }}>
                                  {new Date(tournament.startDate).toLocaleDateString()} - {new Date(tournament.endDate).toLocaleDateString()}
                                </Typography>
                              </Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <LocationOn sx={{ mr: 1, fontSize: 16, color: '#1976d2' }} />
                                <Typography variant="body2" sx={{ fontSize: '0.8rem', color: '#4a5568', fontWeight: 500 }}>{tournament.location}</Typography>
                              </Box>
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <People sx={{ mr: 1, fontSize: 16, color: '#1976d2' }} />
                                <Typography variant="body2" sx={{ fontSize: '0.8rem', color: '#4a5568', fontWeight: 500 }}>
                                  {getCurrentRegistrations(tournament.id)}/{tournament.maxParticipants} participants
                                </Typography>
                              </Box>
                              {tournament.registrationFee && (
                                <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                                  <AttachMoney sx={{ mr: 1, fontSize: 16, color: '#1976d2' }} />
                                  <Typography variant="body2" sx={{ fontSize: '0.8rem', color: '#4a5568', fontWeight: 500 }}>
                                    Registration Fee: {tournament.currencyCode === 'USD' ? '$' : ''}{tournament.registrationFee} {tournament.currencyCode || 'USD'}
                                  </Typography>
                                </Box>
                              )}
                            </Box>

                            <Box sx={{ mb: 2 }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                <Typography variant="body2" sx={{ color: '#4a5568', fontWeight: 600 }}>
                                  Registration Progress
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#1976d2', fontWeight: 700 }}>
                                  {getFillPercentage(tournament)}%
                                </Typography>
                              </Box>
                              <LinearProgress 
                                variant="determinate" 
                                value={getFillPercentage(tournament)} 
                                sx={{ 
                                  height: 8, 
                                  borderRadius: 4,
                                  backgroundColor: 'rgba(25, 118, 210, 0.1)',
                                  '& .MuiLinearProgress-bar': {
                                    background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
                                    borderRadius: 4
                                  }
                                }}
                              />
                            </Box>

                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                              <Typography variant="body2" sx={{ color: '#4a5568', fontWeight: 500 }}>
                                {tournament.status === 'active' ? 'Active Tournament' : 'Inactive Tournament'}
                              </Typography>
                              <Chip 
                                label={tournament.status === 'active' ? 'Active' : 'Inactive'} 
                                sx={{
                                  fontWeight: 600,
                                  background: tournament.status === 'active' 
                                    ? 'linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)'
                                    : 'linear-gradient(135deg, #9e9e9e 0%, #bdbdbd 100%)',
                                  color: 'white',
                                  border: 'none',
                                  boxShadow: tournament.status === 'active' 
                                    ? '0 2px 8px rgba(76, 175, 80, 0.3)'
                                    : '0 2px 8px rgba(158, 158, 158, 0.3)'
                                }}
                                size="small"
                              />
                            </Box>
                            
                            {/* Spacer to push buttons to bottom */}
                            <Box sx={{ flexGrow: 1 }} />
                          </CardContent>
                          
                          <CardActions sx={{ p: 3, pt: 0, mt: 'auto' }}>
                            <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
                              <Button 
                                size="small" 
                                startIcon={<Edit />}
                                onClick={() => handleEditTournament(tournament)}
                                variant="outlined"
                                sx={{ 
                                  flex: 1,
                                  fontWeight: 600,
                                  borderRadius: 2,
                                  textTransform: 'none',
                                  borderColor: '#1976d2',
                                  color: '#1976d2',
                                  '&:hover': {
                                    borderColor: '#1565c0',
                                    backgroundColor: 'rgba(25, 118, 210, 0.08)',
                                    transform: 'translateY(-1px)'
                                  },
                                  transition: 'all 0.3s ease'
                                }}
                              >
                                Edit
                              </Button>
                              <Button 
                                size="small" 
                                startIcon={<Delete />}
                                onClick={() => handleDeleteTournament(tournament)}
                                variant="outlined"
                                sx={{ 
                                  flex: 1,
                                  fontWeight: 600,
                                  borderRadius: 2,
                                  textTransform: 'none',
                                  borderColor: '#f44336',
                                  color: '#f44336',
                                  '&:hover': {
                                    borderColor: '#d32f2f',
                                    backgroundColor: 'rgba(244, 67, 54, 0.08)',
                                    transform: 'translateY(-1px)'
                                  },
                                  transition: 'all 0.3s ease'
                                }}
                              >
                                Delete
                              </Button>
                            </Box>
                          </CardActions>
                        </Card>
                      </motion.div>
                    </Grid>
                    ))
                  ) : (
                    <Grid item xs={12} sx={{ textAlign: 'center', py: 4 }}>
                      <Typography variant="h6">No tournaments available at the moment.</Typography>
                    </Grid>
                  )}
                </Grid>
              </Box>
            </Box>
          </motion.div>

          {/* Create/Edit Tournament Dialog */}
          <Dialog 
            open={dialogOpen} 
            onClose={() => setDialogOpen(false)}
            maxWidth="md"
            fullWidth
            fullScreen={isMobile}
          >
            <DialogTitle sx={{ textAlign: 'center', pb: 2 }}>
              {/* Full Width Divider with Logo */}
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                mb: 3,
                position: 'relative',
                width: '100%'
              }}>
                {/* Left line - extends to edge */}
                <Box sx={{
                  height: '2px',
                  flex: 1,
                  background: 'linear-gradient(to right, transparent, #e2e8f0)',
                  mr: 2
                }} />
                
                {/* Logo */}
                <img
                  src={require('../images/new_logo.png')}
                  alt="VeiledKnights Logo"
                  style={{
                    width: '60px',
                    height: '60px',
                    objectFit: 'contain',
                    borderRadius: '0px'
                  }}
                />
                
                {/* Right line - extends to edge */}
                <Box sx={{
                  height: '2px',
                  flex: 1,
                  background: 'linear-gradient(to right, #e2e8f0, transparent)',
                  ml: 2
                }} />
              </Box>
              
              <Typography variant="h5" sx={{ 
                fontWeight: 700,
                color: '#1a365d',
                background: 'linear-gradient(135deg, #1a365d 0%, #1976d2 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}>
                {editingTournament ? 'Edit Tournament' : 'Create New Tournament'}
              </Typography>
            </DialogTitle>
            <DialogContent>
              {/* Scroll indicator */}
              <Box sx={{
                position: 'sticky',
                top: 0,
                zIndex: 10,
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(10px)',
                borderBottom: '1px solid #e0e0e0',
                py: 1.5,
                mb: 2,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 0.5
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" sx={{ 
                    color: '#1976d2', 
                    fontWeight: 500,
                    fontSize: '0.875rem'
                  }}>
                    Scroll down to see all fields
                  </Typography>
                  <Box sx={{
                    animation: 'bounce 2s infinite',
                    '@keyframes bounce': {
                      '0%, 20%, 50%, 80%, 100%': {
                        transform: 'translateY(0)'
                      },
                      '40%': {
                        transform: 'translateY(-4px)'
                      },
                      '60%': {
                        transform: 'translateY(-2px)'
                      }
                    }
                  }}>
                    ↓
                  </Box>
                </Box>
                <Typography variant="caption" sx={{ 
                  color: '#666', 
                  fontSize: '0.75rem',
                  textAlign: 'center',
                  fontStyle: 'italic'
                }}>
                  Complete all required fields to create tournament
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
                <TextField
                  fullWidth
                  label="Tournament Name"
                  value={tournamentData.name}
                  onChange={(e) => setTournamentData({...tournamentData, name: e.target.value})}
                  required
                />
                
                <FormControl fullWidth required variant="outlined">
                  <InputLabel>Sport</InputLabel>
                  <Select
                    value={tournamentData.sport}
                    onChange={(e) => setTournamentData({...tournamentData, sport: e.target.value})}
                    label="Sport"
                  >
                    {sports.map((sport) => (
                      <MenuItem key={sport} value={sport}>{sport}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                
                <TextField
                  fullWidth
                  label="Start Date"
                  type="date"
                  value={tournamentData.startDate}
                  onChange={(e) => setTournamentData({...tournamentData, startDate: e.target.value})}
                  InputLabelProps={{ shrink: true }}
                  required
                />
                
                <TextField
                  fullWidth
                  label="End Date"
                  type="date"
                  value={tournamentData.endDate}
                  onChange={(e) => setTournamentData({...tournamentData, endDate: e.target.value})}
                  InputLabelProps={{ shrink: true }}
                  required
                />
                
                <TextField
                  fullWidth
                  label="Location"
                  value={tournamentData.location}
                  onChange={(e) => setTournamentData({...tournamentData, location: e.target.value})}
                  required
                />
                
                <TextField
                  fullWidth
                  label="Max Participants"
                  type="number"
                  value={tournamentData.maxParticipants}
                  onChange={(e) => setTournamentData({...tournamentData, maxParticipants: e.target.value})}
                  required
                />
                
                <TextField
                  fullWidth
                  label="Registration Deadline"
                  type="date"
                  value={tournamentData.registrationDeadline}
                  onChange={(e) => setTournamentData({...tournamentData, registrationDeadline: e.target.value})}
                  InputLabelProps={{ shrink: true }}
                  required
                />
                
                <FormControl fullWidth required variant="outlined">
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={tournamentData.status}
                    onChange={(e) => setTournamentData({...tournamentData, status: e.target.value})}
                    label="Status"
                  >
                    {statuses.map((status) => (
                      <MenuItem key={status} value={status}>{status}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                
                <TextField
                  fullWidth
                  label="Description"
                  multiline
                  rows={3}
                  value={tournamentData.description}
                  onChange={(e) => setTournamentData({...tournamentData, description: e.target.value})}
                />
                
                {/* Payment Section */}
                <Box sx={{ 
                  border: '1px solid #e0e0e0', 
                  borderRadius: 2, 
                  p: 3, 
                  backgroundColor: '#f8f9fa',
                  mt: 2
                }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: '#1a365d' }}>
                    💳 Registration Payment Settings
                  </Typography>
                  
                  <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                    <TextField
                      fullWidth
                      label="Registration Fee"
                      type="number"
                      value={tournamentData.registrationFee}
                      onChange={(e) => setTournamentData({...tournamentData, registrationFee: e.target.value})}
                      inputProps={{ min: 0, step: 0.01 }}
                      helperText="Enter the registration fee amount"
                    />
                    
                    <FormControl fullWidth variant="outlined">
                      <InputLabel>Currency</InputLabel>
                      <Select
                        value={tournamentData.currencyCode}
                        onChange={(e) => setTournamentData({...tournamentData, currencyCode: e.target.value})}
                        label="Currency"
                      >
                        {currencies.map((currency) => (
                          <MenuItem key={currency} value={currency}>{currency}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                  
                  <FormControl fullWidth variant="outlined" sx={{ mb: 3 }}>
                    <InputLabel>Payment System</InputLabel>
                    <Select
                      value={tournamentData.registrationFeePaymentSystem}
                      onChange={(e) => setTournamentData({...tournamentData, registrationFeePaymentSystem: e.target.value})}
                      label="Payment System"
                    >
                      {paymentSystems.map((system) => (
                        <MenuItem key={system} value={system}>{system}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  
                  <TextField
                    fullWidth
                    label="Payment URL"
                    value={tournamentData.registrationFeePaymentUrl}
                    onChange={(e) => setTournamentData({...tournamentData, registrationFeePaymentUrl: e.target.value})}
                    placeholder="https://enroll.zellepay.com/qr-codes?data=..."
                    helperText="Enter the payment URL (e.g., Zelle QR code URL, PayPal link, etc.)"
                  />
                </Box>
                
                <Box sx={{ mb: 1 }}>
                  <Typography variant="body2" color="warning.main" sx={{ fontWeight: 500, mb: 1 }}>
                    ⚠️ Important: Replace <Box component="span" sx={{ 
                      color: 'red', 
                      fontWeight: 'bold', 
                      backgroundColor: '#ffebee', 
                      padding: '2px 4px', 
                      borderRadius: '3px',
                      fontFamily: 'monospace',
                      fontSize: '0.9em'
                    }}>&lt;ADD_STATE_HERE&gt;</Box> in the text below with the actual state name (e.g., "California", "Texas", "New York") or modify the sentence to be more appropriate for your tournament location.
                  </Typography>
                </Box>
                
                <TextField
                  fullWidth
                  label="Waiver Text"
                  multiline
                  rows={12}
                  value={tournamentData.waiver}
                  onChange={(e) => setTournamentData({...tournamentData, waiver: e.target.value})}
                  placeholder={DEFAULT_WAIVER_TEXT}
                  helperText="This text will be shown to participants when they register for this tournament"
                />
                
                <Box sx={{ mb: 1 }}>
                  <Typography variant="body2" color="warning.main" sx={{ fontWeight: 500, mb: 1 }}>
                    ⚠️ Important: Replace <Box component="span" sx={{ 
                      color: 'red', 
                      fontWeight: 'bold', 
                      backgroundColor: '#ffebee', 
                      padding: '2px 4px', 
                      borderRadius: '3px',
                      fontFamily: 'monospace',
                      fontSize: '0.9em'
                    }}>&lt;ADD_STATE_HERE&gt;</Box> in the text below with the actual state name (e.g., "California", "Texas", "New York") or modify the sentence to be more appropriate for your tournament location.
                  </Typography>
                </Box>
                
                <TextField
                  fullWidth
                  label="Acknowledgment Text"
                  multiline
                  rows={4}
                  value={tournamentData.acknowledgment}
                  onChange={(e) => setTournamentData({...tournamentData, acknowledgment: e.target.value})}
                  placeholder={DEFAULT_ACKNOWLEDGMENT_TEXT}
                  helperText="This text will be used as the label for the consent checkbox during registration"
                />
                
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={tournamentData.publicViewEnabled}
                      onChange={(e) => setTournamentData({...tournamentData, publicViewEnabled: e.target.checked})}
                      color="primary"
                    />
                  }
                  label="Make tournament visible on public site"
                />
              </Box>
            </DialogContent>
            <DialogActions sx={{ p: 3, pt: 1 }}>
              <Button 
                onClick={() => setDialogOpen(false)}
                sx={{
                  fontWeight: 600,
                  borderRadius: 2,
                  textTransform: 'none',
                  px: 3,
                  py: 1,
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    transform: 'translateY(-1px)'
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                Cancel
              </Button>
              <Button 
                variant="contained" 
                onClick={handleSaveTournament}
                disabled={!tournamentData.name || !tournamentData.sport || !tournamentData.startDate || !tournamentData.endDate || !tournamentData.location || !tournamentData.maxParticipants}
                sx={{
                  fontWeight: 600,
                  borderRadius: 2,
                  textTransform: 'none',
                  px: 3,
                  py: 1,
                  background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                  boxShadow: '0 4px 15px rgba(25, 118, 210, 0.3)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
                    boxShadow: '0 6px 20px rgba(25, 118, 210, 0.4)',
                    transform: 'translateY(-1px)'
                  },
                  '&:disabled': {
                    background: 'rgba(0, 0, 0, 0.12)',
                    color: 'rgba(0, 0, 0, 0.26)',
                    boxShadow: 'none'
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                {editingTournament ? 'Update' : 'Create'}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Delete Confirmation Dialog */}
          <Dialog 
            open={deleteDialogOpen} 
            onClose={cancelDelete}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle sx={{ pb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Delete color="error" />
                <Typography variant="h6" component="span">
                  Delete Tournament
                </Typography>
              </Box>
            </DialogTitle>
            <DialogContent>
              <Alert severity="warning" sx={{ mb: 2 }}>
                This action cannot be undone.
              </Alert>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Are you sure you want to delete the tournament:
              </Typography>
              {tournamentToDelete && (
                <Box sx={{ 
                  p: 2, 
                  backgroundColor: '#f5f5f5', 
                  borderRadius: 1, 
                  border: '1px solid #e0e0e0',
                  mb: 2
                }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                    {tournamentToDelete.name}
                  </Typography>
                  <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                    <strong>Sport:</strong> {tournamentToDelete.sport}
                  </Typography>
                  <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                    <strong>Dates:</strong> {new Date(tournamentToDelete.startDate).toLocaleDateString()} - {new Date(tournamentToDelete.endDate).toLocaleDateString()}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    <strong>Location:</strong> {tournamentToDelete.location}
                  </Typography>
                </Box>
              )}
              <Typography variant="body2" color="textSecondary">
                All tournament data, including registrations, will be permanently removed.
              </Typography>
            </DialogContent>
            <DialogActions sx={{ p: 3, pt: 1 }}>
              <Button 
                onClick={cancelDelete}
                variant="outlined"
                sx={{ 
                  mr: 1,
                  fontWeight: 600,
                  borderRadius: 2,
                  textTransform: 'none',
                  px: 3,
                  py: 1,
                  borderColor: '#1976d2',
                  color: '#1976d2',
                  '&:hover': {
                    borderColor: '#1565c0',
                    backgroundColor: 'rgba(25, 118, 210, 0.08)',
                    transform: 'translateY(-1px)'
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                Cancel
              </Button>
              <Button 
                onClick={confirmDelete}
                variant="contained"
                startIcon={<Delete />}
                sx={{
                  fontWeight: 600,
                  borderRadius: 2,
                  textTransform: 'none',
                  px: 3,
                  py: 1,
                  background: 'linear-gradient(135deg, #f44336 0%, #d32f2f 100%)',
                  boxShadow: '0 4px 15px rgba(244, 67, 54, 0.3)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #d32f2f 0%, #b71c1c 100%)',
                    boxShadow: '0 6px 20px rgba(244, 67, 54, 0.4)',
                    transform: 'translateY(-1px)'
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                Delete Tournament
              </Button>
            </DialogActions>
          </Dialog>
        </Container>
      </Box>
    </Box>
  );
};

export default TournamentManagement;
